import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { inventoryCategoriesApi } from '../inventoryApi';
import type { InventoryCategoryListItem, InventoryItemKind } from '../inventoryTypes';
import { ITEM_KIND_DESCRIPTION, ITEM_KIND_LABEL } from '../inventoryTypes';
import { sampleInventoryCategory } from '../utils/sampleInventory';

/**
 * Create or edit one inventory category.
 *
 * No order field: the position is assigned by the server in gaps of ten and an edit never
 * touches it — a rename is not a reorder, and a form that could express one would eventually
 * perform one by accident.
 */
export function InventoryCategoryModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: InventoryCategoryListItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Seeded once, on mount. The parent mounts this only while it is open.
  const [name, setName] = useState(editing?.name ?? '');
  const [code, setCode] = useState(editing?.code ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [defaultKind, setDefaultKind] = useState<string>(
    editing?.defaultKind === null || editing?.defaultKind === undefined
      ? ''
      : String(editing.defaultKind),
  );

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const valid = name.trim().length > 0;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      name: name.trim(),
      code: code.trim() || null,
      description: description.trim() || null,
      defaultKind: defaultKind === '' ? null : (Number(defaultKind) as InventoryItemKind),
    };

    try {
      if (editing) await inventoryCategoriesApi.update(editing.id, request);
      else await inventoryCategoriesApi.create(request);

      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the category.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'inventory-category' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit category' : 'New category'}
      size="lg"
      id="inventory-category-modal"
      autogenerate={{
        onGenerate: () => {
          const sample = sampleInventoryCategory();
          setName(sample.name);
          setCode(sample.code);
          setDescription(sample.description);
        },
        disabled: saving,
      }}
      confirm={{
        id: 'inventory-category-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="inventory-category-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="inventory-category-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="inventory-category-name"
            label="Name"
            required
            description="What the category is called, e.g. ICT Equipment. Held once among the live rows."
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={150}
            disabled={saving}
            error={error?.fieldError('name')}
          />

          <TextField
            id="inventory-category-code"
            label="Code"
            description="Optional short code, e.g. ICT. Also held once among the live rows — unlike the name, a repeat is refused rather than absorbed."
            value={code}
            onChange={(e) => setCode(e.target.value)}
            maxLength={30}
            disabled={saving}
            error={error?.fieldError('code')}
          />
        </div>

        <TextField
          id="inventory-category-description"
          label="Description"
          description="Optional note for an administrator reading the catalog."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          disabled={saving}
          error={error?.fieldError('description')}
        />

        {/*
          A SUGGESTION, and the help text says so outright. It pre-selects the kind on the item
          form and constrains nothing — a consumable toner cartridge filed under ICT Equipment
          is a real record, not a mistake to refuse.
        */}
        <Select
          id="inventory-category-default-kind"
          label="Usual item kind"
          description="Pre-selects the kind on the item form when this category is chosen. It never restricts what an item here may be — leave it empty to have the form ask."
          placeholder="The form asks"
          value={defaultKind}
          onChange={setDefaultKind}
          sort="none"
          disabled={saving}
          options={Object.entries(ITEM_KIND_LABEL).map(([value, label]) => ({
            value,
            label,
            sublabel: ITEM_KIND_DESCRIPTION[Number(value) as InventoryItemKind],
          }))}
          error={error?.fieldError('defaultKind')}
        />
      </div>
    </Modal>
  );
}
